import { getCookie, setCookie } from '~/lib/.client/cookie';
import { clientFetch } from '~/lib/.client/fetch';
import { CLIENT_ORIGIN } from '~/lib/constants';
import { getToken } from '~/lib/stores/token';

interface TrackingParams {
  // query params
  rdt_cid?: string;
  gclid?: string;
  fbclid?: string;

  // utm query params
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_term?: string;

  // facebook cookie params
  fbc?: string;
  fbp?: string;

  // first matching non-utm query param
  tracking_id?: string;
}

const TRACKING_DATA_KEY = 'trackingData';
const TRACKING_DATA_SENT_KEY = 'trackingDataSent';
const TRACKING_DATA_TOKEN_KEY = 'trackingDataToken';

const UTM_SOURCE_MAP: Record<string, string> = {
  rdt_cid: 'reddit',
  gclid: 'google',
  fbclid: 'facebook',
};

export function captureTrackingParams(): void {
  const searchString = window.location.search;
  const currentParams = extractParamsFromQuery(searchString);
  const enhancedParams = inferSourceFromParams(currentParams);
  const filteredParams = filterUndefinedValues(enhancedParams);

  if (Object.keys(filteredParams).length === 0) {
    return;
  }

  const trackingId = fetchTrackingId(filteredParams);
  const paramsWithFbData = extractFacebookParams(filteredParams);

  const paramsWithTrackingId = {
    ...paramsWithFbData,
    ...(trackingId && { tracking_id: trackingId }),
  };

  try {
    const currentTrackingData = getTrackingData();
    const isSent = isTrackingDataSent();

    if (isExactMatchInTrackingData(currentTrackingData, paramsWithTrackingId)) {
      return;
    }

    let newTrackingData: TrackingParams[];

    if (isSent) {
      newTrackingData = [paramsWithTrackingId];

      const oldJson = JSON.stringify(currentTrackingData);
      const newJson = JSON.stringify(newTrackingData);

      if (oldJson === newJson) {
        return;
      }

      setTrackingData(newTrackingData);
      setTrackingDataSent(false);
    } else {
      newTrackingData = appendIfNotExists(currentTrackingData, paramsWithTrackingId);
      setTrackingData(newTrackingData);
    }
  } catch (error) {
    console.error('Failed to update tracking data in storage', error);
  }
}

export async function sendAdConversions(token?: string): Promise<void> {
  if (isTrackingDataSent()) {
    return;
  }

  try {
    const data = getTrackingData();

    if (data.length === 0) {
      return;
    }

    let url = `${CLIENT_ORIGIN}/api/ad_conversions`;

    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }

    const authorizationToken = await getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (authorizationToken) {
      headers.authorization = `Bearer ${authorizationToken}`;
    }

    const response = await clientFetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(JSON.stringify(error));
    }

    setTrackingDataSent(true);
  } catch (error) {
    console.error('Failed to send ad conversion data', error);
  }
}

export function getTrackingDataToken(): string {
  let token = localStorage.getItem(TRACKING_DATA_TOKEN_KEY);

  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(TRACKING_DATA_TOKEN_KEY, token);
  }

  return token;
}

function getTrackingData(): TrackingParams[] {
  try {
    const data = localStorage.getItem(TRACKING_DATA_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to parse tracking data from storage', error);
    return [];
  }
}

function setTrackingData(data: TrackingParams[]): void {
  localStorage.setItem(TRACKING_DATA_KEY, JSON.stringify(data));
}

function isTrackingDataSent(): boolean {
  return localStorage.getItem(TRACKING_DATA_SENT_KEY) === 'true';
}

function setTrackingDataSent(sent: boolean): void {
  localStorage.setItem(TRACKING_DATA_SENT_KEY, sent ? 'true' : 'false');
}

function extractFacebookParams(params: TrackingParams): TrackingParams {
  const result = { ...params };

  if (!result.fbclid) {
    return result;
  }

  let fbcValue = getCookie('_fbc');

  if (!fbcValue) {
    fbcValue = generateFbc(result.fbclid);

    const expires = new Date();
    expires.setTime(expires.getTime() + 90 * 24 * 60 * 60 * 1000);
    setCookie('_fbc', fbcValue, expires);
  }

  let fbpValue = getCookie('_fbp');

  if (!fbpValue) {
    fbpValue = generateFbp();

    const expires = new Date();
    expires.setTime(expires.getTime() + 90 * 24 * 60 * 60 * 1000);
    setCookie('_fbp', fbpValue, expires);
  }

  if (fbcValue) {
    result.fbc = fbcValue;
  }

  if (fbpValue) {
    result.fbp = fbpValue;
  }

  return result;
}

function extractParamsFromQuery(searchString: string): TrackingParams {
  const queryParams = Object.fromEntries(new URLSearchParams(searchString));

  return {
    rdt_cid: queryParams.rdt_cid,
    gclid: queryParams.gclid,
    fbclid: queryParams.fbclid,
    utm_source: queryParams.utm_source,
    utm_campaign: queryParams.utm_campaign,
    utm_content: queryParams.utm_content,
    utm_medium: queryParams.utm_medium,
    utm_term: queryParams.utm_term,
  };
}

function inferSourceFromParams(params: TrackingParams): TrackingParams {
  const enhancedParams = { ...params };

  if (!enhancedParams.utm_source) {
    for (const [key, value] of Object.entries(UTM_SOURCE_MAP)) {
      if (enhancedParams[key as keyof TrackingParams]) {
        enhancedParams.utm_source = value;
        break;
      }
    }
  }

  return enhancedParams;
}

function filterUndefinedValues(params: TrackingParams): TrackingParams {
  return Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined)) as TrackingParams;
}

function fetchTrackingId(params: TrackingParams): string | undefined {
  const keys = Object.keys(params).filter((key) => !key.startsWith('utm_'));
  return keys.map((key) => params[key as keyof TrackingParams]).find((value) => value);
}

function appendIfNotExists(currentData: TrackingParams[], newParams: TrackingParams): TrackingParams[] {
  const exists = currentData.some((params) => {
    if (newParams.tracking_id) {
      return params.utm_source === newParams.utm_source && params.tracking_id === newParams.tracking_id;
    }

    return params.utm_source === newParams.utm_source && !params.tracking_id;
  });

  if (exists) {
    return currentData;
  }

  return [...currentData, newParams];
}

function isExactMatchInTrackingData(data: TrackingParams[], params: TrackingParams): boolean {
  return data.some((existingParams) => {
    return Object.entries(params).every(([key, value]) => {
      return existingParams[key as keyof TrackingParams] === value;
    });
  });
}

function generateFbc(fbclid: string): string {
  const subdomainIndex = 1;
  const creationTime = Date.now();

  return `fb.${subdomainIndex}.${creationTime}.${fbclid}`;
}

function generateFbp(): string {
  const subdomainIndex = 1;
  const creationTime = Date.now();
  const randomNum = Math.floor(Math.random() * 1e10);

  return `fb.${subdomainIndex}.${creationTime}.${randomNum}`;
}
